import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { ALFRED_SETTINGS_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of Alfred's settings screen — `alfredSettingsInSymphony`.
 *
 * Variant (`true`): Manage on the connected Outreach Alfred row opens `/alfred`. Control (`false`): Manage is
 * the chat handoff, as before the screen existed.
 *
 * It FAILS CLOSED to CONTROL, and like `alfred-onboarding-flag` a FAILED read is not cached: control this
 * time, asked again next time, so one slow read does not send every Manage of the page session to chat. A
 * real answer stays cached.
 *
 * Two readers: the row reads it at the CLICK (`alfredSettingsEnabled`, warmed on mount), so a Manage pressed
 * before the gate answered still lands on the arm the release named; the page reads it to decide whether to
 * stay (`useAlfredSettingsEnabled`). A dev-tools **Feature toggles** override wins over the conducted value,
 * and is inert without a dev-tools grant. The settings read conducts the same toggle on the server.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function alfredSettingsEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(ALFRED_SETTINGS_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/alfred-settings`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) {
        cached = null;
        return false;
      }
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control — this time. Not latched.
      cached = null;
      return false;
    }
  })();
  return cached;
}

/** The arm, for the page: `null` until the gate answers, so the page neither draws nor leaves before it. */
export function useAlfredSettingsEnabled(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    void alfredSettingsEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
